import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import clsx from 'clsx'
import { Box, InputAdornment, TextField, styled, useMediaQuery } from '@mui/material'
import { IconButton } from '@carto/meridian-ds/components'
import CloseIcon from '@mui/icons-material/Close'
import { useGeocoderWidgetController } from '@carto/react-widgets'
import { setCredentials as cartoReactSetCredentials } from '@carto/react-redux'
import { API_VERSIONS } from '@carto/react-api'
import { useAppDispatch, useAppSelector } from 'app/state'
import SearchIcon from 'assets/icons/icon-action-search.svg?react'
import settings from 'settings'
import useAccessToken from 'features/common/ui/useAccessToken'
import { cssDesktopBreakpoint } from 'features/builder/ui/useMobileBuilder'

const GeocoderWidgetWrapper = styled('div')(({ theme }) => ({
  '&:not(.mobile)': {
    transition: theme.transitions.create('width'),
    width: theme.spacing(20),
    '&.expanded': {
      width: theme.spacing(32)
    }
  }
}))
const GeocoderTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    paddingLeft: `${theme.spacing(1)} !important`,
    paddingRight: `${theme.spacing(0.25)} !important`,
    backgroundColor: theme.palette.common.white,
    boxShadow: theme.shadows[1],
    '&.disabled': {
      backgroundColor: theme.palette.background.default
    }
  }
}))

type GeocoderWidgetProps = {
  initialSearchText?: string
  onChange?: (searchText: string) => void
}

function GeocoderWidget(props: GeocoderWidgetProps = {}) {
  const [focused, setFocused] = useState(false)
  const intl = useIntl()
  const placeholderText = intl.formatMessage({ id: 'builder.searchAddress.placeholder' })

  const dispatch = useAppDispatch()
  const accessToken = useAccessToken()

  const initialSearchText = props.initialSearchText || ''
  const geocoderResult = useAppSelector((state) => state.carto.geocoderResult)

  const isMobile = !useMediaQuery(cssDesktopBreakpoint)

  useEffect(() => {
    dispatch(
      cartoReactSetCredentials({
        apiVersion: API_VERSIONS.V3,
        accessToken,
        apiBaseUrl: settings.apiBaseUrl
      })
    )
  }, [accessToken, dispatch])

  const {
    searchText,
    loading,
    handleChange,
    handleInput,
    handleKeyPress: handleKeyPressInner,
    handleBlur: handleBlurInner
  } = useGeocoderWidgetController({ allowSearchByCoords: true, initialSearchText })
  const allowNextBlur = useRef(false)

  const handleBlur = () => {
    setFocused(false)
    if (allowNextBlur.current) {
      allowNextBlur.current = false
      handleBlurInner()
      if (props.onChange) {
        props.onChange(searchText)
      }
    }
  }
  const handleFocus = () => {
    setFocused(true)
  }

  const handleCancel = () => {
    handleChange({ target: { value: '' } })
    handleInput({ target: { value: '' } })
    if (props.onChange) {
      props.onChange('')
    }
  }

  const handleCancelPress = (e) => {
    if (e.key === 'Enter') {
      handleCancel()
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      allowNextBlur.current = true
    }
    handleKeyPressInner(e)
  }

  if (!accessToken) {
    return null
  }

  const showCloseIcon = geocoderResult || searchText
  const isExpanded = focused || searchText || loading || geocoderResult
  const isDisabled = !focused && (isMobile || !searchText || loading || geocoderResult)

  return (
    <GeocoderWidgetWrapper className={clsx({ expanded: isExpanded, mobile: isMobile })}>
      <GeocoderTextField
        size="small"
        focused={focused || undefined}
        InputProps={{
          className: clsx({ disabled: isDisabled }),
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: (loading || showCloseIcon) && (
            <InputAdornment position="end">
              <Box sx={{ display: 'flex', mr: 0.25 }}>
                <IconButton
                  onClick={handleCancel}
                  onKeyDown={handleCancelPress}
                  size="small"
                  loading={loading}
                  icon={<CloseIcon />}
                  tooltip={intl.formatMessage({ id: 'builder.searchAddress.cancelButton' })}
                />
              </Box>
            </InputAdornment>
          )
        }}
        placeholder={placeholderText}
        value={searchText}
        onChange={handleChange}
        onInput={handleInput}
        onKeyDown={handleKeyPress}
        onBlur={handleBlur}
        onFocus={handleFocus}
      />
    </GeocoderWidgetWrapper>
  )
}

export default GeocoderWidget
